<template>
  <div :class="['modifier-groups__group', { 'modifier-groups__group--invalid': showInvalidState && !validateSelections() }]">
    <p class="title title--section title--section-light layout layout--horizontal">
      <span>{{ label }}</span>

      <span class="flex justify-between gap">
        <span
          v-if="modifierGroupSelectionInstructionsText"
          class="smaller no-spacing"
        >
          {{ modifierGroupSelectionInstructionsText }}
        </span>

        <span
          v-if="modiferQuantityText"
          class="smaller no-spacing"
        >
          {{ modiferQuantityText }}
        </span>
      </span>
    </p>

    <div
      :class="[
        'checkbox-group',
        {
          'checkbox-group--read-only': readOnly,
          'checkbox-group--radio': isSingleSelect,
          'checkbox-group--radio-required': isSingleSelect && isRequired,
          'checkbox-group--invalid': showInvalidState && !validateSelections()
        }]"
    >
      <div class="checkbox-group__items">
        <template v-for="modifier in modifierGroup.modifiers">
          <div
            v-if="selections[modifier.menuItemId]"
            :key="modifier.menuItemId"
            :class="['checkbox-group__item', {
              'checkbox-group__item--arrow': checkIsComboModifier(modifier),
              'checkbox-group__item--selected': modifierHasSelectedNestedModifiers(modifier)
            }]"
          >
            <template v-if="checkIsComboModifier(modifier)">
              <!-- "Combo" modifier button selection -->
              <button
                type="button"
                class="checkbox-group__label"
                :aria-invalid="validateSelections()"
                @click="optionClickHandler(modifier)"
              >
                <!-- Modifier Img -->
                <div
                  v-if="modifier.images && modifier.images[0]"
                  class="checkbox-group__label-image"
                  :style="`background-image: url(${ modifier.images[0].url })`"
                />

                <div class="checkbox-group__label-content">
                  <!-- Modifier Name -->
                  <span class="checkbox-group__label-title primary">
                    {{ modifier.displayName }}
                  </span>

                  <span
                    v-if="getCalories(modifier)"
                    class="checkbox-group__label-description"
                  >
                    {{ getCalories(modifier) }}
                  </span>

                  <!-- Modifier's selected nested modifiers -->
                  <p class="smaller no-wrap-ellipsis">
                    {{ getModifierContent(modifier).selectedModifiersDisplay }}
                  </p>
                </div>
              </button>
            </template>

            <template v-else>
              <!-- Checkbox selector -->
              <input
                :id="getInputName(modifier)"
                v-model="selections[modifier.menuItemId].isSelected"
                class="checkbox-group__input"
                type="checkbox"
                :name="getInputName(modifier)"
                :aria-invalid="!validateSelections()"
                :disabled="disableInput(modifier)"
                @change="optionClickHandler(modifier)"
              >
              <label
                class="checkbox-group__label"
                :for="getInputName(modifier)"
              >
                <!-- Modifier Img -->
                <div
                  v-if="modifier.images && modifier.images[0]"
                  class="checkbox-group__label-image"
                  :style="`background-image: url(${ modifier.images[0].url })`"
                />

                <div class="checkbox-group__label-content">
                  <!-- Modifier Name -->
                  <span class="checkbox-group__label-title">
                    {{ modifier.displayName }}
                  </span>

                  <span
                    v-if="getCalories(modifier)"
                    class="checkbox-group__label-description"
                  >
                    {{ getCalories(modifier) }}
                  </span>
                </div>

                <!-- Modifier Decrement/Increment quantity selector -->
                <transition name="fade-left-only-in">
                  <quantity-input
                    v-if="showModifierQuantityInput(modifier)"
                    v-model="selections[modifier.menuItemId].quantity"
                    class="no-spacing"
                    :max-value="getModifierMaxAllowedValue(modifier)"
                    name="menuItemQuantity"
                    size="small"
                  />
                </transition>

                <!-- Modifier price -->
                <span
                  v-if="modifier.price > 0 && !hidePrices"
                  class="checkbox-group__label-description spacing-left"
                >
                  +{{ $dollars(getModifierContent(modifier).totalPrice) }}
                </span>
              </label>
            </template>
          </div>
        </template>
      </div>
    </div>

    <modal
      v-slot="{ data }"
      :name="modalName"
    >
      <menu-item-child
        :allow-deselect="true"
        :item="data.item"
        :read-only="readOnly"
        :selected-modifiers="data.selectedModifiers"
        :selected-quantity="data.selectedQuantity"
        @close="$closeModal(modalName)"
        @deselect="deselect"
        @update="updatedChildHandler"
      />
    </modal>
  </div>
</template>

<script>
  export default {
    name: 'ModifierGroupSelection',

    inject: {
      $validator: {
        from: '$validator'
      },

      validatedForm: {
        from: 'validatedForm'
      }
    },

    props: {
      hidePrices: {
        type: Boolean,
        default: false
      },

      label: {
        type: String,
        required: true
      },

      modifierGroup: {
        type: Object,
        required: true
      },

      name: {
        type: String,
        required: true
      },

      readOnly: {
        type: Boolean,
        default: false
      },

      selectedModifiers: {
        type: Array,
        default: null
      }
    },

    data() {
      return {
        inputIdUniqueifier: Date.now(),
        selections: {},
        showInvalidState: false
      };
    },

    computed: {
      isRequired() {
        return this.modifierGroup.minRequired > 0;
      },

      isSingleSelect() {
        return this.modifierGroup.maxAllowed === 1;
      },

      isUnlimited() {
        return this.modifierGroup.maxAllowed === -1;
      },

      modifierGroupSelectionInstructionsText() {
        const hasMaxQuantitySelection = !this.isRequired && !this.isUnlimited;

        const hasRangeQuantitySelection = this.isRequired && !this.isUnlimited;

        const hasMinQuantitySelection = this.isRequired && this.isUnlimited;

        const { maxAllowed, minRequired } = this.modifierGroup;

        switch (true) {
          case this.isSingleSelect:
            return this.isRequired ? 'Required' : 'Select One';

          case hasMaxQuantitySelection:
            return `Up to ${maxAllowed}`;

          case hasMinQuantitySelection:
            return `At least ${minRequired}`;

          case hasRangeQuantitySelection:
            return `Between ${minRequired} and ${maxAllowed}`;

          default:
            return '';
        }
      },

      modifierCounts() {
        return Object.values(this.selections).reduce((counts, { isSelected, quantity }) => {
          if (isSelected) {
            counts.selection += 1;
            counts.quantity += quantity;
          }

          return counts;
        }, { selection: 0, quantity: 0 });
      },

      modiferQuantityText() {
        const renderQuantityText = this.modifierGroup.maxAllowed >= 1 && this.modifierGroup.maxModifierQtyAllowed > 1 && this.modifierGroup.minModifierQtyRequired >= 1;

        return renderQuantityText && !this.readOnly ? `${this.modifierCounts.quantity} of ${this.modifierGroup.maxModifierQtyAllowed} selected` : '';
      },

      modalName() {
        return `${this.name}-menuItemChild`;
      },

      noneModifierMenuItemId() {
        return this.modifierGroup.modifiers.find(modifier => modifier.modifierTemplate === 'NoneModifierTemplate')?.menuItemId;
      }
    },

    watch: {
      selections: {
        handler: 'selectionsChangeHandler',
        deep: true
      }
    },

    mounted() {
      this.createdHandler();
    },

    unmounted() {
      this.destroyedHandler();
    },

    methods: {
      createdHandler() {
        this.parseModifierGroup();

        this.validatedForm.addValidator({
          name: this.name,
          validate: this.validate
        });
      },

      destroyedHandler() {
        this.validatedForm.removeValidator(this.name);
      },

      getCalories(modifier) {
        return modifier.nutritionalInfo?.calories;
      },

      checkIsComboModifier(modifier) {
        return this.selections[modifier.menuItemId] && modifier.modifierGroups?.length > 0;
      },

      modifierHasSelectedNestedModifiers(modifier) {
        return this.selections[modifier.menuItemId]?.modifiers?.length;
      },

      getModifierContent(modifier) {
        const modifierContent = {
          totalPrice: modifier.price,
          selectedModifiersDisplay: 'Select to customize...'
        };

        const selectedNestedModifiers = this.selections[modifier.menuItemId]?.modifiers;

        if (selectedNestedModifiers?.length) {
          let allMenuModifiers = [];

          modifier.modifierGroups.forEach((group) => {
            allMenuModifiers = allMenuModifiers.concat(group.modifiers);
          });

          modifierContent.selectedModifiersDisplay = '';

          selectedNestedModifiers.forEach((selectedMod) => {
            const menuModifier = allMenuModifiers.find(m => m.menuItemId === selectedMod.menuItemId);

            const modifierQuantitySubstring = selectedMod.quantity > 1 ? ` (${selectedMod.quantity})` : '';

            modifierContent.selectedModifiersDisplay += `${menuModifier.description}${modifierQuantitySubstring}, `;
            modifierContent.totalPrice += menuModifier.price * selectedMod.quantity;
          });
        }

        modifierContent.selectedModifiersDisplay = modifierContent.selectedModifiersDisplay.replace(/,\s$/, '');

        return modifierContent;
      },

      showModifierQuantityInput(modifier) {
        const { maxModifierQtyAllowed } = this.modifierGroup;
        const { maxAllowed } = modifier;
        const allowsModifierGroupQuantity = maxModifierQtyAllowed > 1 || maxModifierQtyAllowed === -1;
        const allowsModifierQuantity = maxAllowed > 1 || maxAllowed === -1;

        return !this.readOnly && allowsModifierGroupQuantity && allowsModifierQuantity && this.selections[modifier.menuItemId].isSelected;
      },

      getModifierMaxAllowedValue(modifier) {
        /** maxModifierQtyAllowed === -1 means no limit so fallback to the modifier's maxAllowed  */
        if (this.modifierGroup.maxModifierQtyAllowed === -1) return modifier.maxAllowed;

        const currentModifierQuantity = this.selections[modifier.menuItemId].quantity;

        const totalQuantityRemainder = this.modifierGroup.maxModifierQtyAllowed - this.modifierCounts.quantity;

        const modifierAvailableMax = totalQuantityRemainder + currentModifierQuantity;

        return Math.min(modifier.maxAllowed, modifierAvailableMax);
      },

      disableInput(modifier) {
        const { isSelected } = this.selections[modifier.menuItemId];

        const { maxModifierQtyAllowed, maxAllowed } = this.modifierGroup;

        const isSingleQuantity = maxModifierQtyAllowed === 1;

        const isUnlimitedQuantity = maxModifierQtyAllowed === -1;

        const hasReachedMaxQuantity = !isSingleQuantity && !isUnlimitedQuantity && maxModifierQtyAllowed <= this.modifierCounts.quantity;

        const hasReachedMaxSelection = !this.isSingleSelect && !this.isUnlimited && maxAllowed <= this.modifierCounts.selection;

        const isNoneModifier = modifier.menuItemId === this.noneModifierMenuItemId;

        return isSelected
          ? this.isSingleSelect && this.isRequired
          : ((hasReachedMaxSelection || hasReachedMaxQuantity) && !isNoneModifier);
      },

      optionClickHandler(modifier) {
        Object.keys(this.selections).forEach((key) => {
          /** if the "none modifier" is selected, deselect all other modifiers */
          if (modifier.menuItemId === this.noneModifierMenuItemId) {
            if (Number(key) !== Number(modifier.menuItemId)) {
              this.selections[key].isSelected = false;
              delete this.selections[key].modifiers;
            }
          }
          /** unselect the "none modifier" if one exists when any other modifier is selected */
          else if (this.selections[key].isNoneModifier) {
            this.selections[key].isSelected = false;
          }

          /* reset the quantity of all non-selected modifiers to 1 */
          if (!this.selections[key].isSelected) {
            this.selections[key].quantity = 1;
          }

          /* if only a single modifier selection is allowed, deselect all other modifiers */
          if (this.isSingleSelect && Number(key) !== Number(modifier.menuItemId)) {
            this.selections[key].isSelected = false;
          }
        });

        if (modifier.modifierGroups?.length > 0) {
          const existingSelection = this.selections[modifier.menuItemId];

          this.$openModal(this.modalName, {
            item: modifier,
            selectedModifiers: existingSelection ? existingSelection.modifiers : null,
            selectedQuantity: existingSelection ? existingSelection.quantity : null
          });
        }
      },

      deselect(menuItemId) {
        this.updateSelection(menuItemId, { quantity: 1, isSelected: false });

        this.$closeModal(this.modalName);
      },

      updatedChildHandler(child) {
        if (child.modifiers?.length) {
          this.updateSelection(child.item.menuItemId, {
            modifiers: child.modifiers,
            quantity: child.quantity,
            isSelected: true
          });
        }
        else {
          this.updateSelection(child.item.menuItemId, {
            quantity: child.quantity,
            isSelected: false
          });
        }

        this.$closeModal(this.modalName);
      },

      resetSelections(prevSelections) {
        const updatedSelections = {};

        Object.keys(prevSelections).forEach((key) => {
          updatedSelections[key] = { isSelected: false, quantity: 1 };
        });

        return updatedSelections;
      },

      updateSelection(menuItemId, value) {
        if (this.isSingleSelect) {
          this.selections = this.resetSelections(this.selections);
        }

        this.selections[menuItemId] = value;

        /*
          NOTE: The following change event dispatch will trigger the "dirty" handler for
          <validate-form>, which will unset the favorite button if the item was a favorite.
        */

        const event = new Event('change', { bubbles: true });

        this.$el.dispatchEvent(event);
      },

      parseModifierGroup() {
        this.selections = this.parseModifierGroupSelections(this.modifierGroup);
      },

      parseModifierGroupSelections(modifierGroup) {
        const selections = {};

        modifierGroup.modifiers.forEach((modifier) => {
          const isNoneModifier = modifier.menuItemId === this.noneModifierMenuItemId;
          let isSelected = false;
          let quantity = 1;
          let modifiers = null;


          /**
           * Sets the selection states of the modifiers based on the existing
           * modifier selections of the order item being edited.
           */
          if (this.selectedModifiers) {
            const selectedModifier = this.selectedModifiers.find(m => m.menuItemId === modifier.menuItemId);

            if (selectedModifier) {
              isSelected = true;

              quantity = selectedModifier.quantity;

              if (selectedModifier.modifiers?.length) {
                modifiers = selectedModifier.modifiers;
              }
            }
          }

          else if (modifier.isDefault) {
            isSelected = true;
          }

          selections[modifier.menuItemId] = {
            isSelected,
            quantity,
            isNoneModifier,
            ...modifiers && { modifiers }
          };
        });

        return selections;
      },

      getInputName(modifier) {
        return `modifier-${this.name}-${this.inputIdUniqueifier}-${modifier.menuItemId}`;
      },

      selectionsChangeHandler(selections) {
        const selectedModifiers = Object.entries(selections).reduce((array, [menuItemId, selection]) => {
          const { isSelected, modifiers, quantity } = selection;

          if (isSelected) {
            array.push({
              menuItemId: Number(menuItemId),
              ...modifiers && { modifiers },
              quantity
            });
          }

          return array;
        }, []);

        this.$emit('selected', {
          modifierGroupId: this.modifierGroup.modifierGroupId,
          modifiers: selectedModifiers
        });
      },

      validate() {
        this.showInvalidState = true;

        return this.validateSelections();
      },

      validateSelections() {
        const maxAllowedSelectedGroupModifiers = this.modifierGroup.maxAllowed === -1 ? Number.MAX_VALUE : this.modifierGroup.maxAllowed;

        const isValidModifierSelection = this.modifierCounts.selection >= this.modifierGroup.minRequired && this.modifierCounts.selection <= maxAllowedSelectedGroupModifiers;

        /** maxModifierQtyAllowed === -1 results in no limit, so max modifier will always be valid, else compare selected modifier quantity against maxModifierQtyAllowed. */
        const isValidMaxModifierQuantity = this.modifierGroup.maxModifierQtyAllowed === -1 || this.modifierCounts.quantity <= this.modifierGroup.maxModifierQtyAllowed;

        /** minModifierQtyRequired === 0 results in no limit, so min modifier will always be valid, else compare selected modifier quantity against minModifierQtyRequired.  */
        const isValidMinModifierQuantity = this.modifierGroup.minModifierQtyRequired === 0 || this.modifierCounts.quantity >= this.modifierGroup.minModifierQtyRequired;

        return isValidModifierSelection && isValidMaxModifierQuantity && isValidMinModifierQuantity;
      }
    }
  };
</script>
